import React, { useState } from "react";
import { Key, Lock, Eye, EyeOff, ShieldCheck, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { decryptData } from "../../utils/crypto";
import { changePassword } from "../../services/authServices";
import Utils from "../../utils";
import { notify } from "../../components/Common/Alerts";
import { useNavigate } from "react-router-dom";

const ChangePassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [showPasswords, setShowPasswords] = useState({
    current_password: false,
    password: false,
    confirm_password: false,
  });
  const [formData, setFormData] = useState({
    password: "",
    confirm_password: "",
    current_password: "",
  });
  const [errors, setErrors] = useState({
    password: "",
    confirm_password: "",
    current_password: "",
  });
  const [btnLoading, setBtnLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value, {
        ...formData,
        [name]: value,
      }),
    }));
  };

  const validateField = (name, value, formData) => {
    switch (name) {
      case "current_password":
        if (!value) return t("currentPasswordRequired");
        return "";

      case "password":
        if (!value) return t("passwordRequired");
        if (value.length < 6) return t("passwordCharactersLong");
        if (!/[a-zA-Z]/.test(value)) return t("passwordMustLetter");
        return "";

      case "confirm_password":
        if (!value) return t("confirmPasswordIsRequired");
        if (value !== formData?.password) return t("passwordsDoNotMatch");
        return "";

      default:
        return "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {
      current_password: validateField(
        "current_password",
        formData.current_password,
        formData,
      ),
      password: validateField("password", formData.password, formData),
      confirm_password: validateField(
        "confirm_password",
        formData.confirm_password,
        formData,
      ),
    };

    setErrors(newErrors);

    const hasError = Object.values(newErrors).some((err) => err);
    if (hasError) return;

    try {
      setBtnLoading(true);

      const payload = {
        old_password: formData.current_password,
        new_password: formData.password,
      };

      const response = await changePassword(payload);
      const decrypted = decryptData(response.data.data);

      notify.success(decrypted?.message || "Password updated");

      setFormData({
        current_password: "",
        password: "",
        confirm_password: "",
      });
      navigate("/dashboard");
      setErrors({});
    } catch (err) {
      notify.error(Utils.getErrorMessage(err));
    } finally {
      setBtnLoading(false);
    }
  };

  const toggleVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <div className="bg-[#FDFDFD] dark:bg-slate-950 p-4 sm:p-8 md:p-12 transition-colors duration-300">
      <div className="hidden md:flex items-center gap-4 mb-10">
        {/* Back Button */}
        <button
          type="button"
          onClick={() => {
            navigate("/dashboard");
          }}
          className="p-2.5 rounded-xl bg-white dark:bg-slate-900 shadow-sm border border-slate-100 dark:border-slate-800 text-slate-400 hover:text-brand-orange transition-colors"
        >
          <ArrowLeft size={20} />
        </button>

        {/* Shield Icon Container - Removed mb-6 and adjusted size */}
        <div className="w-11 h-11 bg-orange-50 dark:bg-orange-950/20 rounded-xl flex items-center justify-center text-brand-orange border border-orange-100/50 shadow-inner shrink-0">
          <ShieldCheck size={24} strokeWidth={2} />
        </div>

        {/* Header Title - Aligned perfectly with icons */}
        <h1 className="text-2xl font-bold text-slate-800 dark:text-white tracking-tight leading-none">
          {t("updatePassword")}
        </h1>
      </div>

      <div className="w-full bg-white dark:bg-slate-900 rounded-[2.5rem] p-8 md:p-12 shadow-[0_20px_50px_rgba(0,0,0,0.03)] border border-slate-50 dark:border-slate-800 relative overflow-hidden">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          <div className="flex-1">
            <form className="space-y-8" onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                <div className="md:col-span-2 space-y-2 group">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
                    {t("currentPassword")}
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                      <Key size={18} />
                    </div>
                    <input
                      type={
                        showPasswords.current_password ? "text" : "password"
                      }
                      name="current_password"
                      value={formData.current_password}
                      onChange={handleChange}
                      placeholder="••••••••"
                      className="w-full bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700 rounded-2xl py-4 pl-12 pr-12 focus:ring-2 focus:ring-brand-orange/20 focus:border-brand-orange outline-none transition-all text-slate-700 dark:text-slate-200"
                    />
                    <button
                      type="button"
                      onClick={() => toggleVisibility("current_password")}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-brand-orange transition-colors"
                    >
                      {showPasswords.current_password ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                  {errors.current_password && (
                    <p className="text-red-500 text-xs mt-1 ml-2">
                      {errors.current_password}
                    </p>
                  )}
                </div>

                <div className="space-y-2 group">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
                    {t("newPassword")}
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                      <Key size={18} />
                    </div>
                    <input
                      type={showPasswords.password ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Min. 8 characters"
                      className="w-full bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700 rounded-2xl py-4 pl-12 pr-12 focus:ring-2 focus:ring-brand-orange/20 focus:border-brand-orange outline-none transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => toggleVisibility("password")}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-brand-orange"
                    >
                      {showPasswords.password ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-red-500 text-xs mt-1 ml-2">
                      {errors.password}
                    </p>
                  )}
                </div>

                <div className="space-y-2 group">
                  <label className="text-sm font-semibold text-slate-700 dark:text-slate-300 ml-1">
                    {t("confirmPassword")}
                  </label>
                  <div className="relative">
                    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400">
                      <Key size={18} />
                    </div>
                    <input
                      type={
                        showPasswords.confirm_password ? "text" : "password"
                      }
                      name="confirm_password"
                      value={formData.confirm_password}
                      onChange={handleChange}
                      placeholder="Repeat new password"
                      className="w-full bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700 rounded-2xl py-4 pl-12 pr-12 focus:ring-2 focus:ring-brand-orange/20 focus:border-brand-orange outline-none transition-all"
                    />
                    <button
                      type="button"
                      onClick={() => toggleVisibility("confirm_password")}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-brand-orange"
                    >
                      {showPasswords.confirm_password ? (
                        <EyeOff size={18} />
                      ) : (
                        <Eye size={18} />
                      )}
                    </button>
                  </div>
                  {errors.confirm_password && (
                    <p className="text-red-500 text-xs mt-1 ml-2">
                      {errors.confirm_password}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <button
                  type="submit"
                  disabled={btnLoading}
                  className="px-10 py-4 bg-brand-orange hover:bg-orange-600 text-white font-bold rounded-2xl shadow-lg shadow-orange-500/20 active:scale-[0.98] transition-all duration-200 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-3"
                >
                  {btnLoading ? (
                    <>
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      {t("updatingPassword")}
                    </>
                  ) : (
                    t("updatePassword")
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
